import { useGetPatientTreatmentEpisodesQuery } from '~/store/services/clinicalData';
import {
  useGetDiscussionOfWeightAttributesQuery,
  useGetFormTreatmentAttributesQuery,
  useGetLevelTreatmentAttributesQuery,
  useGetMedicalStabilityTreatmentAttributesQuery,
  useGetModalityTreatmentAttributesQuery,
  useGetPhaseStageTreatmentAttributesQuery,
  useGetPsychiatricStabilityTreatmentAttributesQuery,
  useGetRefeedingRiskAttributesQuery,
  useGetSaferStageAttributesQuery,
} from '~/store/services/treatmentAttributes';

// Consolidates fetching of individual treatment attributes
// For performance reasons, use individual queries if you do not need all treatment attributes
export const useTreatmentAttributes = (patientId: string) => {
  const {
    data: patientTreatmentEpisodes,
    isLoading: isPatientTreatmentEpisodesLoading,
  } = useGetPatientTreatmentEpisodesQuery(patientId);
  const activeTreatmentEpisode = patientTreatmentEpisodes?.data.find(
    (episode) => episode.inTreatment,
  );
  const {
    data: discussionOfWeights,
    isLoading: isGetDiscussionOfWeightAttributesLoading,
  } = useGetDiscussionOfWeightAttributesQuery(
    activeTreatmentEpisode?.treatmentEpisodeId,
    { skip: !activeTreatmentEpisode },
  );

  const { data: levels, isLoading: isGetLevelsLoading } =
    useGetLevelTreatmentAttributesQuery(
      activeTreatmentEpisode?.treatmentEpisodeId,
      { skip: !activeTreatmentEpisode },
    );

  const { data: saferStages, isLoading: isGetSaferStagesLoading } =
    useGetSaferStageAttributesQuery(
      activeTreatmentEpisode?.treatmentEpisodeId,
      { skip: !activeTreatmentEpisode },
    );

  const { data: refeedingRisks, isLoading: isGetRefeedingRisksLoading } =
    useGetRefeedingRiskAttributesQuery(
      activeTreatmentEpisode?.treatmentEpisodeId,
      { skip: !activeTreatmentEpisode },
    );

  const { data: modalities, isLoading: isGetModalitiesLoading } =
    useGetModalityTreatmentAttributesQuery(
      activeTreatmentEpisode?.treatmentEpisodeId,
      { skip: !activeTreatmentEpisode },
    );

  const { data: forms, isLoading: isGetFormsLoading } =
    useGetFormTreatmentAttributesQuery(
      activeTreatmentEpisode?.treatmentEpisodeId,
      { skip: !activeTreatmentEpisode },
    );

  const { data: phaseStages, isLoading: isGetPhaseStagesLoading } =
    useGetPhaseStageTreatmentAttributesQuery(
      activeTreatmentEpisode?.treatmentEpisodeId,
      { skip: !activeTreatmentEpisode },
    );

  const {
    data: medicalStabilities,
    isLoading: isGetMedicalStabilitiesLoading,
  } = useGetMedicalStabilityTreatmentAttributesQuery(
    activeTreatmentEpisode?.treatmentEpisodeId,
    { skip: !activeTreatmentEpisode },
  );

  const {
    data: psychiatricStabilities,
    isLoading: isGetPsychiatricStabilitiesLoading,
  } = useGetPsychiatricStabilityTreatmentAttributesQuery(
    activeTreatmentEpisode?.treatmentEpisodeId,
    { skip: !activeTreatmentEpisode },
  );

  const isLoading =
    isPatientTreatmentEpisodesLoading ||
    isGetDiscussionOfWeightAttributesLoading ||
    isGetLevelsLoading ||
    isGetSaferStagesLoading ||
    isGetRefeedingRisksLoading ||
    isGetModalitiesLoading ||
    isGetFormsLoading ||
    isGetPhaseStagesLoading ||
    isGetMedicalStabilitiesLoading ||
    isGetPsychiatricStabilitiesLoading;

  return {
    activeTreatmentEpisode,
    isLoading,
    discussionOfWeights,
    modalities,
    forms,
    phaseStages,
    levels,
    refeedingRisks,
    saferStages,
    medicalStabilities,
    psychiatricStabilities,
  };
};
